import { DashboardPage } from '@pflegenavi/frontend/pages/dashboard';
import {
  recurringItemRoutes,
  residentRoutes,
} from '@pflegenavi/frontend/pages/residents';
import { nhAppMainPages } from '@pflegenavi/frontend/routing';
import { Suspense, useCallback, useEffect, type JSX } from 'react';
import { useSnackbar } from 'notistack';

import * as Sentry from '@sentry/react';

import { Box, CircularProgress, styled } from '@mui/material';
import {
  useEmployeeProfilePhoenix,
  useGetPaymentProcessor,
  useUserProfileApi,
} from '@pflegenavi/frontend/api-nursing-home';
import { useAuthentication } from '@pflegenavi/frontend/authentication';
import { useFeatureFlag } from '@pflegenavi/frontend/feature-flags';
import { useFeatureFlagModal } from '@pflegenavi/frontend/feature-flags-web';
import { DashboardLayout } from '@pflegenavi/frontend/layout';
import {
  DevLangSwitcher,
  localeStorage,
} from '@pflegenavi/frontend/localization-web-app';
import {
  RouteAccessGuard,
  useScope,
} from '@pflegenavi/frontend/nursing-home-context';
import {
  useNursingHomeAccountingRoutes,
  useExpenseManagementRoutes,
  useBudgetsRoutes,
} from '@pflegenavi/frontend/pages/accountingPayments';
import { useCashManagementRoutes } from '@pflegenavi/frontend/pages/cash-management';
import {
  useNursingHomeRoutes,
  MyCardsPage,
} from '@pflegenavi/frontend/pages/nursing-home';
import {
  reportingRoutes,
  reportingV3Routes,
  useReportingV3Visible,
} from '@pflegenavi/frontend/pages/reporting';
import { serviceProviderRoutes } from '@pflegenavi/frontend/pages/service-provider';
import { TestingModal } from '@pflegenavi/frontend/pages/testing';
import {
  IncompleteTransactionsProvider,
  receiptRoutes,
  useReceiptBatchRoutes,
} from '@pflegenavi/frontend/pages/transactions';
import { useTenantId } from '@pflegenavi/frontend/tenant';
import { shouldUseNewLogin } from '@pflegenavi/shared-frontend/authentication';
import {
  Login,
  RedirectResetPassword,
} from '@pflegenavi/shared-web/authentication';
import { FeatureFlag } from '@pflegenavi/shared/api';
import { useSyncLocaleWithServer } from '@pflegenavi/shared/frontend';
import { PageNotFound, Text } from '@pflegenavi/web-components';
import { useTranslation } from 'react-i18next';
import { Navigate, Route, useLocation, useNavigate } from 'react-router-dom';
import { SentryRoutes } from '../sentry';
import { EmployeeModeDropdown } from './components/EmployeeModeDropdown';
import { SelectScope } from '@pflegenavi/frontend/pages/nursing-home';
import { renderSwitchLanguageMenuItem } from './components/SwitchLanguageMenuItem';
import { DevPage } from './DevPage';
import { MenuItems } from './menu/MenuItems';
import { useEmployeeIsNursingHomeAdminOrManager } from '@pflegenavi/frontend/nursing-home-context';

export default function App(): JSX.Element {
  const { t } = useTranslation();
  const { user } = useAuthentication();
  const navigate = useNavigate();
  const location = useLocation();
  const tenant = useTenantId();

  const userProfileApi = useUserProfileApi(true);

  const employeeIsNursingHomeAdminOrManager =
    useEmployeeIsNursingHomeAdminOrManager();

  const { selectedNursingHome } = useScope();

  const { enqueueSnackbar } = useSnackbar();
  const onUnavailablePageInLink = useCallback(() => {
    enqueueSnackbar(t('nav.page-unavailable-in-link'), { variant: 'error' });
  }, [enqueueSnackbar, t]);

  const paymentProcessor = useGetPaymentProcessor();
  const reportingV3Visible = useReportingV3Visible();
  const { data: userProfile } = useEmployeeProfilePhoenix({
    enabled: userProfileApi !== undefined,
    refetchOnWindowFocus: false,
  });

  const accountingRoutes = useNursingHomeAccountingRoutes();
  const expenseManagementRoutes = useExpenseManagementRoutes();
  const budgetsRoutes = useBudgetsRoutes();
  const receiptBatchRoutes = useReceiptBatchRoutes();
  const cashManagementRoutes = useCashManagementRoutes();
  const nursingHomeRoutes = useNursingHomeRoutes();

  useSyncLocaleWithServer({ localeStorage });

  useSetupSentryScopeWithNursingHome();

  const serviceProviderAppFeatureFlag = useFeatureFlag(
    FeatureFlag.dev_ServiceProviderApp
  );

  const { FeatureFlagModalComponent } = useFeatureFlagModal();

  useEffect(() => {
    // @ts-expect-error Beacon is untyped
    if (!window.Beacon || !userProfile || !userProfile.user) {
      return;
    }

    // @ts-expect-error Beacon is untyped
    window.Beacon('identify', {
      name: `${userProfile.user.first_name} ${userProfile.user.last_name}`,
      email: userProfile.user.email ?? '',
      'nursing-home': selectedNursingHome?.name ?? 'Unknown',
      tenant,
      'user-id': userProfile.user.id,
      environment: window.location.hostname.startsWith('localhost')
        ? 'local'
        : tenant === 'default_tenant' || tenant === 'other_tenant'
        ? 'staging'
        : 'production',
      application: 'Nursing home web',
    });
  }, [userProfile, selectedNursingHome, tenant]);

  useEffect(() => {
    // @ts-expect-error Beacon is untyped
    if (!window.Beacon) {
      return;
    }

    // @ts-expect-error Beacon is untyped
    window.Beacon('event', {
      type: 'page-viewed',
      url: location.pathname,
      title: document.title,
    });
    // @ts-expect-error Beacon is untyped
    window.Beacon('suggest');
    if (location && location.pathname === '/') {
      // @ts-expect-error Beacon is untyped
      window.Beacon('navigate', '/');
    }
  }, [location.pathname, location]);

  if (!user || !userProfile) {
    if (shouldUseNewLogin()) {
      return <Login useEmailFirstLogin={true} />;
    }

    return (
      <LoadingContainer>
        <CircularProgress variant="indeterminate" />
      </LoadingContainer>
    );
  }

  return (
    <Suspense
      fallback={
        <Text color="primary" variant="h2">
          {t('')}
        </Text>
      }
    >
      {!import.meta.env.PROD && <DevLangSwitcher />}
      {FeatureFlagModalComponent}
      <IncompleteTransactionsProvider>
        <>
          <TestingModal />
          <SentryRoutes>
            <Route
              path={nhAppMainPages.WELCOME}
              element={<Navigate to={nhAppMainPages.HOME} replace />}
            />
            {/* Required-action pages only exist while logged out (see Login).
                A leftover /required/... URL would otherwise hit the catch-all
                below and render the "something went wrong" page. */}
            <Route
              path={'/required/*'}
              element={<Navigate to={nhAppMainPages.HOME} replace />}
            />
            <Route
              path={'/reset-password/:token'}
              element={<RedirectResetPassword />}
            />
            <Route
              path={nhAppMainPages.HOME}
              element={
                <DashboardLayout
                  UnderLogoComponent={<SelectScope />}
                  configKey="nh-web"
                  home={nhAppMainPages.HOME}
                  menuItems={
                    employeeIsNursingHomeAdminOrManager ? (
                      <MenuItems />
                    ) : undefined
                  }
                  helpUrl={'https://pn.helpscoutdocs.com/'}
                  renderSwitchLanguageMenuItem={renderSwitchLanguageMenuItem}
                  leftSideItems={<EmployeeModeDropdown />}
                />
              }
            >
              <Route
                element={
                  <RouteAccessGuard
                    onUnavailablePageInLink={onUnavailablePageInLink}
                  />
                }
              >
                <Route
                  path={nhAppMainPages.DASHBOARD}
                  element={<DashboardPage />}
                />
                <Route path={nhAppMainPages.DEV_PAGE} element={<DevPage />} />
                {residentRoutes(serviceProviderAppFeatureFlag)}
                {recurringItemRoutes()}
                {serviceProviderRoutes()}
                {receiptRoutes()}
                <Route
                  path={nhAppMainPages.HOME}
                  element={<Navigate to={nhAppMainPages.DASHBOARD} replace />}
                />
                {receiptBatchRoutes}
                {cashManagementRoutes}
                {accountingRoutes}
                {expenseManagementRoutes}
                {budgetsRoutes}
                {paymentProcessor === 'stripe' && reportingRoutes()}
                {reportingV3Visible && reportingV3Routes()}
                {nursingHomeRoutes}
                <Route
                  path={nhAppMainPages.MY_CARDS}
                  element={<MyCardsPage />}
                />
                <Route
                  path="*"
                  element={
                    <PageNotFound
                      handleGoHomeClick={() => navigate(nhAppMainPages.HOME)}
                    />
                  }
                />
              </Route>
            </Route>
          </SentryRoutes>
        </>
      </IncompleteTransactionsProvider>
    </Suspense>
  );
}

const useSetupSentryScopeWithNursingHome = () => {
  const { selectedNursingHome } = useScope();
  const { user } = useAuthentication();

  useEffect(() => {
    if (!selectedNursingHome || !user) {
      return;
    }

    const scope = Sentry.getCurrentScope();
    scope.setUser(
      user
        ? {
            id: user?.userId,
          }
        : null
    );
    scope.setTags({
      nursingHomeId: selectedNursingHome.id,
      nursingHomeName: selectedNursingHome.name,
    });
  }, [selectedNursingHome, user]);
};

const LoadingContainer = styled(Box)(({ theme }) =>
  theme.unstable_sx({
    display: 'flex',
    width: '100%',
    height: '100%',
    justifyContent: 'center',
    alignItems: 'center',
  })
);
